import { Box, Button, Stack, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import { LineChart } from "@mui/x-charts/LineChart";
import { BASE_URL_WAREHOUSE } from "constants/api/routeGlobals";
import { JSX, useEffect, useState } from "react";

export function Dashboard(): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = () => {
    fetch(BASE_URL_WAREHOUSE + "sge/79/stats/daily")
      .then((response) => response.json())
      .then((data) => {
        setData(data);
        setLastRefresh(new Date());
        setLoading(false);
      })
      .catch((error) => {
        console.error("Erreur lors du chargement", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <div>Chargement des données...</div>;
  }

  const repartitionData = data.map((item) => {
      
    if (item.qabreakdown === undefined) {
      return { date: new Date(item.date+"T00:00:00"), weight_fail: 0, dims_fail: 0, lpn_fail: 0, scan_fail: 0, unknown_lpn: 0 };
    }
      
    const faults = item.qabreakdown[0];
    const totalItems =
      faults.weight_fail +
      faults.dims_fail +
      faults.lpn_fail +
      faults.scan_fail +
      faults.unknown_lpn;

    const weight_fail =
      faults.weight_fail > 0 ? (faults.weight_fail / totalItems) * 100 : 0;
    const dims_fail =
      faults.dims_fail > 0 ? (faults.dims_fail / totalItems) * 100 : 0;
    const lpn_fail =
      faults.lpn_fail > 0 ? (faults.lpn_fail / totalItems) * 100 : 0;
    const scan_fail =
      faults.scan_fail > 0 ? (faults.scan_fail / totalItems) * 100 : 0;
    const unknown_lpn =
      faults.unknown_lpn > 0 ? (faults.unknown_lpn / totalItems) * 100 : 0;

    return {
      date: new Date(item.date+"T00:00:00"),
      weight_fail: weight_fail,
      dims_fail: dims_fail,
      lpn_fail: lpn_fail,
      scan_fail: scan_fail,
      unknown_lpn: unknown_lpn
    };
  });
  const rapartitionLabels = repartitionData.map((item) => item.date);

  const tauxQAData = data.map((item) => {
    const qaRate = item.advices > 0 ? (item.qa / item.advices) * 100 : 0;
    return {
      date: new Date(item.date+"T00:00:00"),
      advices: item.advices,
      qaRate: qaRate,
    };
  });

  const operationsData = data.map((item) => { return {
      date: new Date(item.date+"T00:00:00"),
      advices: item.advices,
      orders: item.orders,
      palletmoves: item.palletmoves
    }}); 

  return (
    <>
      <div style={{ height: "100%", overflowY: "auto" }}>
        <Stack
          sx={{ height: "100%" }}
          spacing={2}
          alignItems="center"
          padding={2}
        >
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography variant="h6" component="div">
              Opérations
            </Typography>
            <BarChart
              height={300}
              dataset={operationsData}
              xAxis={[
                {
                  dataKey: "date",
                  valueFormatter: (date: any) =>
                    date.toLocaleDateString("en-CA").substring(5),
                },
              ]}
              series={[
                {
                  type: "bar",
                  dataKey: "advices",
                  label: "Rangements complétés",
                  color: '#b7bf91'
                },
                {
                  type: "bar",
                  dataKey: "orders",
                  label: "Ramassages complétés",
                  color: '#b8d1e9'
                },
                {
                  type: "bar",
                  dataKey: "palletmoves",
                  label: "Déplacements de palettes",
                  color: '#af7dbe'
                },
              ]}
              yAxis={[
                { id: "leftAxisId", width: 50 },
                { id: "rightAxisId", width: 50, position: "right" },
              ]}
            ></BarChart>
          </Box>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography variant="h6" component="div">
              Taux QA
            </Typography>
            <BarChart
              height={300}
              dataset={tauxQAData}
              xAxis={[
                {
                  dataKey: "date",
                  valueFormatter: (date: any) =>
                    date.toLocaleDateString("en-CA").substring(5),
                },
              ]}
              series={[
                {
                  type: "bar",
                  dataKey: "advices",
                  label: "Rangements complétés",
                  yAxisId: "leftAxisId",
                  color: '#b7bf91'
                },
                {
                  type: "bar",
                  dataKey: "qaRate",
                  label: "Taux de QA (%)",
                  yAxisId: "rightAxisId",
                  color: '#e99c55'
                },
              ]}
              yAxis={[
                { id: "leftAxisId", width: 50 },
                { id: "rightAxisId", width: 50, position: "right" },
              ]}
            ></BarChart>
          </Box>
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <Typography variant="h6" component="div">
              Répartition en % des échecs au QA
            </Typography>
            <LineChart
              dataset={repartitionData}
              grid={{ horizontal: true }}
              xAxis={[
                {
                  id: "date",
                  data: rapartitionLabels,
                  dataKey: "date",
                  scaleType: "time",
                  valueFormatter: (date: any) =>
                    date.toLocaleDateString("en-CA").substring(5),
                },
              ]}
              series={[
                {
                  id: "WEIGHT_FAIL",
                  label: "Poids",
                  dataKey: "weight_fail",
                  valueFormatter: (v) => (v === null ? "" : v.toFixed(2)),
                },
                {
                  id: "DIMS_FAIL",
                  label: "Dimensions",
                  dataKey: "dims_fail",
                  valueFormatter: (v) => (v === null ? "" : v.toFixed(2)),
                },
                {
                  id: "LPN_FAIL",
                  label: "LPN",
                  dataKey: "lpn_fail",
                  valueFormatter: (v) => (v === null ? "" : v.toFixed(2)),
                },
                {
                  id: "SCAN_FAIL",
                  label: "Lecture barcode",
                  dataKey: "scan_fail",
                  valueFormatter: (v) => (v === null ? "" : v.toFixed(2)),
                },
                {
                  id: "UNKNOWN_LPN",
                  label: "LPN Inconnu",
                  dataKey: "unknown_lpn",
                  valueFormatter: (v) => (v === null ? "" : v.toFixed(2)),
                },
              ]}
              yAxis={[
                { id: "leftAxisId", width: 50 },
                { id: "rightAxisId", width: 50, position: "right" },
              ]}
              height={300}
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchData()}
            style={{ width: "10em" }}
          >
            Rafraîchir
          </Button>
          {lastRefresh && (
            <div>
              Dernière mise à jour :{" "}
              {lastRefresh.toLocaleString("en-CA", { hourCycle: "h23" })}
            </div>
          )}
        </Stack>
      </div>
    </>
  );
}
